/**
 * teamResolution.js — team / league entity-resolution data + helpers.
 * Extracted verbatim from services/cacheService.js (god-file decomposition).
 * Pure data + pure functions; no side effects, no imports.
 *
 * NOTE (per .cursor/rules/comprehensive-coverage.mdc): the static alias/ambiguity
 * tables here are a *fallback* supplement to the cache-driven DYNAMIC_TEAM_INDEX.
 * They only cover aliases/nicknames that differ from API names. Phase C NLP work
 * should ensure cache lookups remain the primary resolution path.
 */

// Pre-computed lowercase Map for O(1) lookups
export const TEAM_LOOKUP_MAP = new Map();

// City-to-teams mapping (one city can have multiple teams)
export const CITY_TO_TEAMS = {
  // California
  'los angeles': ['Lakers', 'Clippers', 'Dodgers', 'Angels', 'Rams', 'Chargers', 'Kings', 'Ducks', 'Galaxy', 'LAFC', 'Sparks'],
  'la': ['Lakers', 'Clippers', 'Dodgers', 'Angels', 'Rams', 'Chargers', 'Kings', 'Ducks', 'Galaxy', 'LAFC'],
  'san francisco': ['Giants', '49ers', 'Warriors'],
  'sf': ['Giants', '49ers', 'Warriors'],
  'oakland': ['Athletics', 'Raiders'],
  'san diego': ['Padres', 'Chargers'],
  'sacramento': ['Kings'],
  'anaheim': ['Angels', 'Ducks'],
  
  // New York
  'new york': ['Yankees', 'Mets', 'Knicks', 'Nets', 'Rangers', 'Islanders', 'Giants', 'Jets', 'Liberty', 'NYCFC', 'Red Bulls'],
  'ny': ['Yankees', 'Mets', 'Knicks', 'Nets', 'Rangers', 'Islanders', 'Giants', 'Jets'],
  'nyc': ['Yankees', 'Mets', 'Knicks', 'Nets', 'Rangers', 'Islanders'],
  'brooklyn': ['Nets'],
  'queens': ['Mets'],
  'bronx': ['Yankees'],
  
  // Texas
  'dallas': ['Cowboys', 'Mavericks', 'Stars', 'Rangers', 'FC Dallas', 'Wings'],
  'houston': ['Texans', 'Rockets', 'Astros', 'Dynamo', 'Dash'],
  'san antonio': ['Spurs'],
  'austin': ['Austin FC'],
  
  // Florida
  'miami': ['Heat', 'Dolphins', 'Marlins', 'Panthers', 'Inter Miami'],
  'tampa': ['Buccaneers', 'Lightning', 'Rays'],
  'tampa bay': ['Buccaneers', 'Lightning', 'Rays'],
  'orlando': ['Magic', 'Orlando City'],
  'jacksonville': ['Jaguars'],
  
  // Illinois
  'chicago': ['Bulls', 'Bears', 'Cubs', 'White Sox', 'Blackhawks', 'Sky', 'Fire'],
  
  // Massachusetts
  'boston': ['Celtics', 'Red Sox', 'Patriots', 'Bruins', 'Revolution'],
  
  // Pennsylvania
  'philadelphia': ['76ers', 'Sixers', 'Eagles', 'Phillies', 'Flyers', 'Union'],
  'philly': ['76ers', 'Sixers', 'Eagles', 'Phillies', 'Flyers'],
  'pittsburgh': ['Steelers', 'Pirates', 'Penguins'],
  
  // Ohio
  'cleveland': ['Cavaliers', 'Cavs', 'Browns', 'Guardians'],
  'cincinnati': ['Bengals', 'Reds', 'FC Cincinnati'],
  'columbus': ['Blue Jackets', 'Crew'],
  
  // Michigan
  'detroit': ['Lions', 'Tigers', 'Pistons', 'Red Wings'],
  
  // Georgia
  'atlanta': ['Hawks', 'Falcons', 'Braves', 'Atlanta United', 'Dream'],
  
  // Arizona
  'phoenix': ['Suns', 'Cardinals', 'Diamondbacks', 'Coyotes', 'Mercury'],
  
  // Colorado
  'denver': ['Nuggets', 'Broncos', 'Rockies', 'Avalanche', 'Rapids'],
  
  // Washington
  'seattle': ['Seahawks', 'Mariners', 'Kraken', 'Sounders', 'Storm'],
  
  // Oregon
  'portland': ['Trail Blazers', 'Blazers', 'Timbers'],
  
  // Minnesota
  'minneapolis': ['Timberwolves', 'Vikings', 'Twins', 'Wild', 'Lynx'],
  'minnesota': ['Timberwolves', 'Vikings', 'Twins', 'Wild', 'Lynx'],
  
  // Wisconsin
  'milwaukee': ['Bucks', 'Brewers'],
  'green bay': ['Packers'],
  
  // Indiana
  'indianapolis': ['Colts', 'Pacers', 'Fever'],
  'indy': ['Colts', 'Pacers', 'Fever'],
  
  // Missouri
  'kansas city': ['Chiefs', 'Royals', 'Sporting KC'],
  'kc': ['Chiefs', 'Royals', 'Sporting KC'],
  'st louis': ['Cardinals', 'Blues', 'St. Louis City SC'],
  'st. louis': ['Cardinals', 'Blues', 'St. Louis City SC'],
  
  // Tennessee
  'nashville': ['Titans', 'Predators', 'Nashville SC'],
  'memphis': ['Grizzlies'],
  
  // Louisiana
  'new orleans': ['Saints', 'Pelicans'],
  
  // North Carolina
  'charlotte': ['Hornets', 'Panthers', 'Charlotte FC'],
  'raleigh': ['Hurricanes'],
  
  // Maryland/DC
  'washington': ['Commanders', 'Wizards', 'Nationals', 'Capitals', 'Mystics', 'D.C. United'],
  'dc': ['Commanders', 'Wizards', 'Nationals', 'Capitals', 'Mystics'],
  'washington dc': ['Commanders', 'Wizards', 'Nationals', 'Capitals', 'Mystics'],
  'baltimore': ['Ravens', 'Orioles'],
  
  // Nevada
  'las vegas': ['Raiders', 'Golden Knights', 'Aces'],
  'vegas': ['Raiders', 'Golden Knights', 'Aces'],
  
  // Utah
  'salt lake': ['Jazz', 'Real Salt Lake', 'Utah Hockey Club'],
  'salt lake city': ['Jazz', 'Real Salt Lake'],
  
  // Canada
  'toronto': ['Raptors', 'Blue Jays', 'Maple Leafs', 'Toronto FC'],
  'vancouver': ['Canucks', 'Whitecaps'],
  'montreal': ['Canadiens', 'CF Montreal'],
  'calgary': ['Flames'],
  'edmonton': ['Oilers'],
  'ottawa': ['Senators'],
  'winnipeg': ['Jets'],
};

// Aliases and nicknames (maps alias → canonical team name)
export const TEAM_ALIASES = {
  // NBA
  'sixers': '76ers',
  'cavs': 'Cavaliers',
  'blazers': 'Trail Blazers',
  'wolves': 'Timberwolves',
  't-wolves': 'Timberwolves',
  'mavs': 'Mavericks',
  'grizz': 'Grizzlies',
  'clips': 'Clippers',
  'dubs': 'Warriors',
  
  // NFL
  'niners': '49ers',
  'bucs': 'Buccaneers',
  'cards': 'Cardinals',
  'pats': 'Patriots',
  'pack': 'Packers',
  'vikes': 'Vikings',
  // ⭐ N4: 'bolts' removed — ambiguous (Chargers/Lightning); the backend's
  // AMBIGUOUS_TEAM_NICKNAMES clarify gate owns it now.
  'fins': 'Dolphins',
  'birds': 'Eagles',
  'boys': 'Cowboys',
  'skins': 'Commanders',
  'washington football': 'Commanders',
  
  // NHL
  'leafs': 'Maple Leafs',
  'habs': 'Canadiens',
  'avs': 'Avalanche',
  'pens': 'Penguins',
  'caps': 'Capitals',
  'sens': 'Senators',
  'wings': 'Red Wings',
  'canes': 'Hurricanes',
  'isles': 'Islanders',
  'preds': 'Predators',
  'cbj': 'Blue Jackets',
  'jackets': 'Blue Jackets',
  'knights': 'Golden Knights',
  'vgk': 'Golden Knights',
  
  // MLB
  'jays': 'Blue Jays',
  'sox': 'Red Sox', // Ambiguous - context needed
  'nats': 'Nationals',
  'halos': 'Angels',
  'd-backs': 'Diamondbacks',
  'dbacks': 'Diamondbacks',
  'snakes': 'Diamondbacks',
  "a's": 'Athletics',
  'as': 'Athletics',
  'cubbies': 'Cubs',
  'buccos': 'Pirates',
  'fish': 'Marlins',
  'brew crew': 'Brewers',
  
  // College
  'unc': 'North Carolina',
  'tar heels': 'North Carolina',
  'blue devils': 'Duke',
  'wildcats': 'Kentucky', // Ambiguous
  'jayhawks': 'Kansas',
  'bulldogs': 'Gonzaga', // Ambiguous
  'spartans': 'Michigan State',
  'wolverines': 'Michigan',
  'buckeyes': 'Ohio State',
  'boilermakers': 'Purdue',
  'hoosiers': 'Indiana',
  'fighting illini': 'Illinois',
  'illini': 'Illinois',
  'badgers': 'Wisconsin',
  'hawkeyes': 'Iowa',
  'terps': 'Maryland',
  'terrapins': 'Maryland',
  'longhorns': 'Texas',
  'aggies': 'Texas A&M',
  'sooners': 'Oklahoma',
  'crimson tide': 'Alabama',
  'tide': 'Alabama',
  'vols': 'Tennessee',
  'volunteers': 'Tennessee',
  'gators': 'Florida',
  'razorbacks': 'Arkansas',
  'hogs': 'Arkansas',
  'tigers': 'LSU', // Ambiguous - also Auburn, Clemson, etc.
  'seminoles': 'Florida State',
  'noles': 'Florida State',
  'hurricanes': 'Miami', // Also NHL Carolina
  'canes': 'Miami', // Also NHL Carolina
  'huskies': 'UConn', // Also Washington
  'bruins': 'UCLA', // Also NHL Boston
  'trojans': 'USC',
  'ducks': 'Oregon', // Also NHL Anaheim
  'beavers': 'Oregon State',
  'cougars': 'Washington State', // Also Houston, BYU
  'utes': 'Utah',
  'buffs': 'Colorado',
  'buffaloes': 'Colorado',
  'golden bears': 'California',
  'bears': 'California', // Also NFL Chicago
  'sun devils': 'Arizona State',
  'zags': 'Gonzaga',
};

// Teams that exist in multiple leagues (need context to disambiguate)
export const AMBIGUOUS_TEAMS = {
  'Panthers': ['nfl', 'nhl'], // Carolina Panthers (NFL) vs Florida Panthers (NHL)
  'Cardinals': ['nfl', 'mlb'], // Arizona Cardinals (NFL) vs St. Louis Cardinals (MLB)
  'Giants': ['nfl', 'mlb'], // NY Giants (NFL) vs SF Giants (MLB)
  'Rangers': ['nhl', 'mlb'], // NY Rangers (NHL) vs Texas Rangers (MLB)
  'Kings': ['nba', 'nhl'], // Sacramento Kings (NBA) vs LA Kings (NHL)
  'Jets': ['nfl', 'nhl'], // NY Jets (NFL) vs Winnipeg Jets (NHL)
  'Bruins': ['nhl', 'ncaab'], // Boston Bruins (NHL) vs UCLA Bruins (NCAAB)
  'Ducks': ['nhl', 'ncaab', 'ncaaf'], // Anaheim Ducks (NHL) vs Oregon Ducks
  'Hurricanes': ['nhl', 'ncaab', 'ncaaf'], // Carolina Hurricanes (NHL) vs Miami Hurricanes
  'Stars': ['nhl', 'wnba'], // Dallas Stars (NHL) vs... context needed
  'Lightning': ['nhl'], // Tampa Bay - usually unambiguous
  'Wild': ['nhl', 'wnba'], // Minnesota Wild (NHL)
  'Flames': ['nhl', 'ncaaf'], // Calgary Flames (NHL) vs Liberty Flames
  'Tigers': ['mlb', 'ncaab', 'ncaaf'], // Detroit Tigers, LSU, Clemson, Auburn, etc.
  'Bears': ['nfl', 'ncaab'], // Chicago Bears vs Cal Bears
  'Cougars': ['ncaab', 'ncaaf'], // Houston, BYU, Washington State
  'Wildcats': ['ncaab', 'ncaaf'], // Kentucky, Arizona, K-State, Northwestern
  'Bulldogs': ['ncaab', 'ncaaf'], // Gonzaga, Georgia, Miss State, etc.
  // ⭐ College teams that play BOTH basketball AND football (CFB Playoff makes football more popular)
  'Ole Miss': ['ncaab', 'ncaaf'],
  'Rebels': ['ncaab', 'ncaaf'], // Ole Miss Rebels
  'Missouri': ['ncaab', 'ncaaf'],
  'Mizzou': ['ncaab', 'ncaaf'],
  'Alabama': ['ncaab', 'ncaaf'],
  'Crimson Tide': ['ncaab', 'ncaaf'],
  'Georgia': ['ncaab', 'ncaaf'],
  'Tennessee': ['ncaab', 'ncaaf'],
  'Vols': ['ncaab', 'ncaaf'],
  'Auburn': ['ncaab', 'ncaaf'],
  'Florida': ['ncaab', 'ncaaf'],
  'Gators': ['ncaab', 'ncaaf'],
  'LSU': ['ncaab', 'ncaaf'],
  'Texas': ['ncaab', 'ncaaf'],
  'Longhorns': ['ncaab', 'ncaaf'],
  'Texas A&M': ['ncaab', 'ncaaf'],
  'Aggies': ['ncaab', 'ncaaf'],
  'Oklahoma': ['ncaab', 'ncaaf'],
  'Sooners': ['ncaab', 'ncaaf'],
  'Ohio State': ['ncaab', 'ncaaf'],
  'Buckeyes': ['ncaab', 'ncaaf'],
  'Michigan': ['ncaab', 'ncaaf'],
  'Wolverines': ['ncaab', 'ncaaf'],
  'Penn State': ['ncaab', 'ncaaf'],
  'Nittany Lions': ['ncaab', 'ncaaf'],
  'Notre Dame': ['ncaab', 'ncaaf'],
  'Fighting Irish': ['ncaab', 'ncaaf'],
  'Clemson': ['ncaab', 'ncaaf'],
  'USC': ['ncaab', 'ncaaf'],
  'Trojans': ['ncaab', 'ncaaf'],
  'Oregon': ['ncaab', 'ncaaf'],
  'Washington': ['ncaab', 'ncaaf'],
  'Huskies': ['ncaab', 'ncaaf'],
  'Rams': ['nfl'], // LA Rams - only NFL (no ambiguity)
};

// Market-to-sport mapping (helps disambiguate)
export const MARKET_SPORT_HINTS = {
  // Hockey-specific
  'goals': 'hockey',
  'saves': 'hockey',
  'shots on goal': 'hockey',
  'sog': 'hockey',
  'power play': 'hockey',
  'penalty minutes': 'hockey',
  'pim': 'hockey',
  'faceoffs': 'hockey',
  'period': 'hockey', // Can also be basketball quarters
  
  // Baseball-specific
  'hits': 'baseball',
  'home runs': 'baseball',
  'hr': 'baseball',
  'rbis': 'baseball',
  'rbi': 'baseball',
  'runs': 'baseball',
  'strikeouts': 'baseball',
  'innings': 'baseball',
  'era': 'baseball',
  'batting average': 'baseball',
  'stolen bases': 'baseball',
  'walks': 'baseball',
  
  // Football-specific
  'passing yards': 'football',
  'rushing yards': 'football',
  'receiving yards': 'football',
  'touchdowns': 'football',
  'tds': 'football',
  'interceptions': 'football',
  'sacks': 'football',
  'completions': 'football',
  'attempts': 'football',
  'receptions': 'football',
  'targets': 'football',
  'yards after catch': 'football',
  'yac': 'football',
  'first downs': 'football',
  'field goals': 'football', // Also basketball
  'extra points': 'football',
  
  // Basketball-specific
  'points': 'basketball',
  'assists': 'basketball',
  'rebounds': 'basketball',
  'blocks': 'basketball',
  'steals': 'basketball',
  'turnovers': 'basketball',
  'threes': 'basketball',
  '3-pointers': 'basketball',
  'free throws': 'basketball',
  'double-double': 'basketball',
  'triple-double': 'basketball',
  'minutes': 'basketball',
  
  // Soccer-specific
  'clean sheet': 'soccer',
  'yellow cards': 'soccer',
  'red cards': 'soccer',
  'corners': 'soccer',
  'offsides': 'soccer',
  'possession': 'soccer',
};

/**
 * 🔤 Simple Levenshtein distance for fuzzy matching
 * Returns the edit distance between two strings
 */
export function levenshteinDistance(str1, str2) {
  const m = str1.length;
  const n = str2.length;
  
  // Quick checks
  if (m === 0) return n;
  if (n === 0) return m;
  if (str1 === str2) return 0;
  
  // Create distance matrix
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,      // deletion
        dp[i][j - 1] + 1,      // insertion
        dp[i - 1][j - 1] + cost // substitution
      );
    }
  }
  
  return dp[m][n];
}

/**
 * 🔍 Fuzzy match team name with typo tolerance
 * Returns { match: string, confidence: number, distance: number } or null
 */
export function fuzzyMatchTeam(input, maxDistance = 2) {
  const inputLower = input.toLowerCase().trim();
  
  // First check exact match (O(1) with Map)
  if (TEAM_LOOKUP_MAP.has(inputLower)) {
    return { match: TEAM_LOOKUP_MAP.get(inputLower).canonical, confidence: 1.0, distance: 0 };
  }
  
  // Check aliases
  const aliasLower = inputLower;
  if (TEAM_ALIASES[aliasLower]) {
    return { match: TEAM_ALIASES[aliasLower], confidence: 0.95, distance: 0 };
  }
  
  // Fuzzy match - only if input is at least 4 chars (avoid false positives)
  if (inputLower.length < 4) return null;
  
  let bestMatch = null;
  let bestDistance = maxDistance + 1;
  
  for (const [teamLower, info] of TEAM_LOOKUP_MAP.entries()) {
    // Skip very different length strings
    if (Math.abs(teamLower.length - inputLower.length) > maxDistance) continue;
    
    const distance = levenshteinDistance(inputLower, teamLower);
    if (distance <= maxDistance && distance < bestDistance) {
      bestDistance = distance;
      bestMatch = info.canonical;
    }
  }
  
  if (bestMatch) {
    // Confidence decreases with distance
    const confidence = 1 - (bestDistance / (inputLower.length + 1));
    return { match: bestMatch, confidence, distance: bestDistance };
  }
  
  return null;
}

/**
 * 🎯 Disambiguate team based on context
 * Uses market type, query keywords, and seasonality
 */
export function disambiguateTeam(teamName, context = {}) {
  const { market, query = '', preferredSport = null } = context;
  const queryLower = query.toLowerCase();
  
  // Check if this team is ambiguous
  const ambiguousLeagues = AMBIGUOUS_TEAMS[teamName];
  if (!ambiguousLeagues || ambiguousLeagues.length === 1) {
    // Not ambiguous, return from main mapping
    return TEAM_TO_LEAGUE[teamName] || null;
  }
  
  // Try to disambiguate using market hints
  if (market) {
    const marketLower = market.toLowerCase();
    for (const [marketKeyword, sport] of Object.entries(MARKET_SPORT_HINTS)) {
      if (marketLower.includes(marketKeyword)) {
        // Find which league matches this sport
        for (const league of ambiguousLeagues) {
          const leagueInfo = getLeagueSport(league);
          if (leagueInfo && leagueInfo.sport === sport) {
            console.log(`[Disambiguate] "${teamName}" → ${league} (market hint: ${marketKeyword})`);
            return { league, sport };
          }
        }
      }
    }
  }
  
  // Try to disambiguate using explicit league keywords in query
  for (const league of ambiguousLeagues) {
    if (queryLower.includes(league)) {
      const leagueInfo = getLeagueSport(league);
      console.log(`[Disambiguate] "${teamName}" → ${league} (explicit keyword)`);
      return leagueInfo;
    }
  }
  
  // Try to disambiguate using sport keywords
  const sportKeywords = {
    'hockey': 'nhl',
    'baseball': 'mlb',
    'football': 'nfl',
    'basketball': 'nba',
    'soccer': 'mls',
    'college basketball': 'ncaab',
    'college football': 'ncaaf',
  };
  
  for (const [keyword, league] of Object.entries(sportKeywords)) {
    if (queryLower.includes(keyword) && ambiguousLeagues.includes(league)) {
      const leagueInfo = getLeagueSport(league);
      console.log(`[Disambiguate] "${teamName}" → ${league} (sport keyword: ${keyword})`);
      return leagueInfo;
    }
  }
  
  // Use preferred sport if provided
  if (preferredSport) {
    for (const league of ambiguousLeagues) {
      const leagueInfo = getLeagueSport(league);
      if (leagueInfo && leagueInfo.sport === preferredSport) {
        console.log(`[Disambiguate] "${teamName}" → ${league} (preferred sport)`);
        return leagueInfo;
      }
    }
  }
  
  // Seasonality-based disambiguation (rough heuristic)
  const month = new Date().getMonth(); // 0-11
  const inSeason = {
    nhl: month >= 9 || month <= 5, // Oct-June
    mlb: month >= 2 && month <= 9, // March-Oct
    nfl: month >= 8 || month <= 1, // Sept-Feb
    nba: month >= 9 || month <= 5, // Oct-June
    ncaab: month >= 10 || month <= 3, // Nov-April
    ncaaf: month >= 7 || month <= 0, // Aug-Jan (FIXED: was using && which never matched)
  };
  
  // ⭐ CFB Playoff Priority: In December/January, prefer college football over basketball
  // Since CFB playoffs get massive attention and betting interest
  const isCFBPlayoffTime = month === 11 || month === 0; // Dec-Jan
  if (isCFBPlayoffTime && ambiguousLeagues.includes('ncaaf') && ambiguousLeagues.includes('ncaab')) {
    console.log(`[Disambiguate] "${teamName}" → ncaaf (CFB playoff season priority)`);
    return { league: 'ncaaf', sport: 'football' };
  }
  
  // Prefer leagues that are in season
  for (const league of ambiguousLeagues) {
    if (inSeason[league]) {
      const leagueInfo = getLeagueSport(league);
      console.log(`[Disambiguate] "${teamName}" → ${league} (in season)`);
      return leagueInfo;
    }
  }
  
  // Default to first option
  const defaultLeague = ambiguousLeagues[0];
  console.log(`[Disambiguate] "${teamName}" → ${defaultLeague} (default)`);
  return getLeagueSport(defaultLeague);
}

/**
 * Helper to get sport from league
 */
export function getLeagueSport(league) {
  const leagueSports = {
    nba: { league: 'nba', sport: 'basketball' },
    wnba: { league: 'wnba', sport: 'basketball' },
    nfl: { league: 'nfl', sport: 'football' },
    nhl: { league: 'nhl', sport: 'hockey' },
    mlb: { league: 'mlb', sport: 'baseball' },
    mls: { league: 'mls', sport: 'soccer' },
    ncaab: { league: 'ncaab', sport: 'basketball' },
    ncaaf: { league: 'ncaaf', sport: 'football' },
  };
  return leagueSports[league] || null;
}

/**
 * 🏙️ Find teams by city name
 * Returns array of possible teams with their leagues
 */
export function findTeamsByCity(cityName) {
  const cityLower = cityName.toLowerCase().trim();
  const teams = CITY_TO_TEAMS[cityLower];
  
  if (!teams) return [];
  
  return teams.map(teamName => ({
    team: teamName,
    ...TEAM_TO_LEAGUE[teamName]
  })).filter(t => t.league); // Only return teams we have mappings for
}

/**
 * 🏀🏈🏒⚾ Comprehensive Team-to-League Mapping
 * Maps team names to their respective leagues and sports
 */
export const TEAM_TO_LEAGUE = {
  // === NBA (30 teams) ===
  'Lakers': { league: 'nba', sport: 'basketball' },
  'Celtics': { league: 'nba', sport: 'basketball' },
  'Warriors': { league: 'nba', sport: 'basketball' },
  'Heat': { league: 'nba', sport: 'basketball' },
  'Nets': { league: 'nba', sport: 'basketball' },
  'Bucks': { league: 'nba', sport: 'basketball' },
  'Suns': { league: 'nba', sport: 'basketball' },
  'Clippers': { league: 'nba', sport: 'basketball' },
  'Nuggets': { league: 'nba', sport: 'basketball' },
  'Mavericks': { league: 'nba', sport: 'basketball' },
  'Grizzlies': { league: 'nba', sport: 'basketball' },
  'Pelicans': { league: 'nba', sport: 'basketball' },
  'Kings': { league: 'nba', sport: 'basketball' },
  'Timberwolves': { league: 'nba', sport: 'basketball' },
  'Thunder': { league: 'nba', sport: 'basketball' },
  'Trail Blazers': { league: 'nba', sport: 'basketball' },
  'Blazers': { league: 'nba', sport: 'basketball' },
  'Jazz': { league: 'nba', sport: 'basketball' },
  'Rockets': { league: 'nba', sport: 'basketball' },
  'Spurs': { league: 'nba', sport: 'basketball' },
  'Hawks': { league: 'nba', sport: 'basketball' },
  'Hornets': { league: 'nba', sport: 'basketball' },
  'Bulls': { league: 'nba', sport: 'basketball' },
  'Cavaliers': { league: 'nba', sport: 'basketball' },
  'Cavs': { league: 'nba', sport: 'basketball' },
  'Pistons': { league: 'nba', sport: 'basketball' },
  'Pacers': { league: 'nba', sport: 'basketball' },
  'Magic': { league: 'nba', sport: 'basketball' },
  '76ers': { league: 'nba', sport: 'basketball' },
  'Sixers': { league: 'nba', sport: 'basketball' },
  'Knicks': { league: 'nba', sport: 'basketball' },
  'Raptors': { league: 'nba', sport: 'basketball' },
  'Wizards': { league: 'nba', sport: 'basketball' },

  // === WNBA (12 teams) ===
  'Aces': { league: 'wnba', sport: 'basketball' },
  'Lynx': { league: 'wnba', sport: 'basketball' },
  'Liberty': { league: 'wnba', sport: 'basketball' },
  'Sun': { league: 'wnba', sport: 'basketball' },
  'Storm': { league: 'wnba', sport: 'basketball' },
  'Sky': { league: 'wnba', sport: 'basketball' },
  'Fever': { league: 'wnba', sport: 'basketball' },
  'Wings': { league: 'wnba', sport: 'basketball' },
  'Sparks': { league: 'wnba', sport: 'basketball' },
  'Mercury': { league: 'wnba', sport: 'basketball' },
  'Mystics': { league: 'wnba', sport: 'basketball' },
  'Dream': { league: 'wnba', sport: 'basketball' },

  // === NFL (32 teams) ===
  'Bills': { league: 'nfl', sport: 'football' },
  'Dolphins': { league: 'nfl', sport: 'football' },
  'Patriots': { league: 'nfl', sport: 'football' },
  'Jets': { league: 'nfl', sport: 'football' },
  'Ravens': { league: 'nfl', sport: 'football' },
  'Bengals': { league: 'nfl', sport: 'football' },
  'Browns': { league: 'nfl', sport: 'football' },
  'Steelers': { league: 'nfl', sport: 'football' },
  'Texans': { league: 'nfl', sport: 'football' },
  'Colts': { league: 'nfl', sport: 'football' },
  'Jaguars': { league: 'nfl', sport: 'football' },
  'Titans': { league: 'nfl', sport: 'football' },
  'Broncos': { league: 'nfl', sport: 'football' },
  'Chiefs': { league: 'nfl', sport: 'football' },
  'Raiders': { league: 'nfl', sport: 'football' },
  'Chargers': { league: 'nfl', sport: 'football' },
  'Cowboys': { league: 'nfl', sport: 'football' },
  'Giants': { league: 'nfl', sport: 'football' },
  'Eagles': { league: 'nfl', sport: 'football' },
  'Commanders': { league: 'nfl', sport: 'football' },
  'Washington': { league: 'nfl', sport: 'football' },
  'Bears': { league: 'nfl', sport: 'football' },
  'Lions': { league: 'nfl', sport: 'football' },
  'Packers': { league: 'nfl', sport: 'football' },
  'Vikings': { league: 'nfl', sport: 'football' },
  'Falcons': { league: 'nfl', sport: 'football' },
  'Panthers': { league: 'nfl', sport: 'football' },
  'Saints': { league: 'nfl', sport: 'football' },
  'Buccaneers': { league: 'nfl', sport: 'football' },
  'Bucs': { league: 'nfl', sport: 'football' },
  '49ers': { league: 'nfl', sport: 'football' },
  'Niners': { league: 'nfl', sport: 'football' },
  'Cardinals': { league: 'nfl', sport: 'football' },
  'Rams': { league: 'nfl', sport: 'football' },
  'Seahawks': { league: 'nfl', sport: 'football' },

  // === NHL (32 teams) ===
  'Bruins': { league: 'nhl', sport: 'hockey' },
  'Sabres': { league: 'nhl', sport: 'hockey' },
  'Red Wings': { league: 'nhl', sport: 'hockey' },
  'Panthers': { league: 'nhl', sport: 'hockey' }, // Florida Panthers - also NFL, will prioritize by context
  'Canadiens': { league: 'nhl', sport: 'hockey' },
  'Habs': { league: 'nhl', sport: 'hockey' },
  'Senators': { league: 'nhl', sport: 'hockey' },
  'Lightning': { league: 'nhl', sport: 'hockey' },
  'Maple Leafs': { league: 'nhl', sport: 'hockey' },
  'Leafs': { league: 'nhl', sport: 'hockey' },
  'Hurricanes': { league: 'nhl', sport: 'hockey' },
  'Blue Jackets': { league: 'nhl', sport: 'hockey' },
  'Devils': { league: 'nhl', sport: 'hockey' },
  'Islanders': { league: 'nhl', sport: 'hockey' },
  'Rangers': { league: 'nhl', sport: 'hockey' },
  'Flyers': { league: 'nhl', sport: 'hockey' },
  'Penguins': { league: 'nhl', sport: 'hockey' },
  'Capitals': { league: 'nhl', sport: 'hockey' },
  'Caps': { league: 'nhl', sport: 'hockey' },
  'Blackhawks': { league: 'nhl', sport: 'hockey' },
  'Avalanche': { league: 'nhl', sport: 'hockey' },
  'Avs': { league: 'nhl', sport: 'hockey' },
  'Stars': { league: 'nhl', sport: 'hockey' },
  'Wild': { league: 'nhl', sport: 'hockey' },
  'Predators': { league: 'nhl', sport: 'hockey' },
  'Preds': { league: 'nhl', sport: 'hockey' },
  'Blues': { league: 'nhl', sport: 'hockey' },
  'Jets': { league: 'nhl', sport: 'hockey' }, // Winnipeg Jets - also NFL
  'Coyotes': { league: 'nhl', sport: 'hockey' },
  'Utah Hockey Club': { league: 'nhl', sport: 'hockey' },
  'Ducks': { league: 'nhl', sport: 'hockey' },
  'Flames': { league: 'nhl', sport: 'hockey' },
  'Oilers': { league: 'nhl', sport: 'hockey' },
  'Kings': { league: 'nhl', sport: 'hockey' }, // LA Kings - also NBA Sacramento Kings
  'Sharks': { league: 'nhl', sport: 'hockey' },
  'Kraken': { league: 'nhl', sport: 'hockey' },
  'Canucks': { league: 'nhl', sport: 'hockey' },
  'Golden Knights': { league: 'nhl', sport: 'hockey' },
  'Knights': { league: 'nhl', sport: 'hockey' },

  // === MLB (30 teams) ===
  'Orioles': { league: 'mlb', sport: 'baseball' },
  'Red Sox': { league: 'mlb', sport: 'baseball' },
  'Yankees': { league: 'mlb', sport: 'baseball' },
  'Rays': { league: 'mlb', sport: 'baseball' },
  'Blue Jays': { league: 'mlb', sport: 'baseball' },
  'Jays': { league: 'mlb', sport: 'baseball' },
  'Guardians': { league: 'mlb', sport: 'baseball' },
  'Twins': { league: 'mlb', sport: 'baseball' },
  'White Sox': { league: 'mlb', sport: 'baseball' },
  'Royals': { league: 'mlb', sport: 'baseball' },
  'Tigers': { league: 'mlb', sport: 'baseball' },
  'Astros': { league: 'mlb', sport: 'baseball' },
  'Angels': { league: 'mlb', sport: 'baseball' },
  'Athletics': { league: 'mlb', sport: 'baseball' },
  "A's": { league: 'mlb', sport: 'baseball' },
  'Mariners': { league: 'mlb', sport: 'baseball' },
  'Rangers': { league: 'mlb', sport: 'baseball' }, // Texas Rangers - also NHL
  'Braves': { league: 'mlb', sport: 'baseball' },
  'Marlins': { league: 'mlb', sport: 'baseball' },
  'Mets': { league: 'mlb', sport: 'baseball' },
  'Phillies': { league: 'mlb', sport: 'baseball' },
  'Nationals': { league: 'mlb', sport: 'baseball' },
  'Nats': { league: 'mlb', sport: 'baseball' },
  'Cubs': { league: 'mlb', sport: 'baseball' },
  'Reds': { league: 'mlb', sport: 'baseball' },
  'Brewers': { league: 'mlb', sport: 'baseball' },
  'Pirates': { league: 'mlb', sport: 'baseball' },
  'Cardinals': { league: 'mlb', sport: 'baseball' }, // STL Cardinals - also NFL Arizona
  'Diamondbacks': { league: 'mlb', sport: 'baseball' },
  'D-backs': { league: 'mlb', sport: 'baseball' },
  'Rockies': { league: 'mlb', sport: 'baseball' },
  'Dodgers': { league: 'mlb', sport: 'baseball' },
  'Padres': { league: 'mlb', sport: 'baseball' },
  'Giants': { league: 'mlb', sport: 'baseball' }, // SF Giants - also NFL

  // === MLS (29 teams) ===
  'Atlanta United': { league: 'mls', sport: 'soccer' },
  'Austin FC': { league: 'mls', sport: 'soccer' },
  'Charlotte FC': { league: 'mls', sport: 'soccer' },
  'Chicago Fire': { league: 'mls', sport: 'soccer' },
  'FC Cincinnati': { league: 'mls', sport: 'soccer' },
  'Colorado Rapids': { league: 'mls', sport: 'soccer' },
  'Rapids': { league: 'mls', sport: 'soccer' },
  'Columbus Crew': { league: 'mls', sport: 'soccer' },
  'Crew': { league: 'mls', sport: 'soccer' },
  'FC Dallas': { league: 'mls', sport: 'soccer' },
  'D.C. United': { league: 'mls', sport: 'soccer' },
  'Houston Dynamo': { league: 'mls', sport: 'soccer' },
  'Dynamo': { league: 'mls', sport: 'soccer' },
  'Sporting Kansas City': { league: 'mls', sport: 'soccer' },
  'Sporting KC': { league: 'mls', sport: 'soccer' },
  'LA Galaxy': { league: 'mls', sport: 'soccer' },
  'Galaxy': { league: 'mls', sport: 'soccer' },
  'LAFC': { league: 'mls', sport: 'soccer' },
  'Inter Miami': { league: 'mls', sport: 'soccer' },
  'CF Montreal': { league: 'mls', sport: 'soccer' },
  'Nashville SC': { league: 'mls', sport: 'soccer' },
  'New England Revolution': { league: 'mls', sport: 'soccer' },
  'Revolution': { league: 'mls', sport: 'soccer' },
  'NYCFC': { league: 'mls', sport: 'soccer' },
  'New York City FC': { league: 'mls', sport: 'soccer' },
  'New York Red Bulls': { league: 'mls', sport: 'soccer' },
  'Red Bulls': { league: 'mls', sport: 'soccer' },
  'Orlando City': { league: 'mls', sport: 'soccer' },
  'Philadelphia Union': { league: 'mls', sport: 'soccer' },
  'Union': { league: 'mls', sport: 'soccer' },
  'Portland Timbers': { league: 'mls', sport: 'soccer' },
  'Timbers': { league: 'mls', sport: 'soccer' },
  'Real Salt Lake': { league: 'mls', sport: 'soccer' },
  'RSL': { league: 'mls', sport: 'soccer' },
  'San Jose Earthquakes': { league: 'mls', sport: 'soccer' },
  'Earthquakes': { league: 'mls', sport: 'soccer' },
  'Seattle Sounders': { league: 'mls', sport: 'soccer' },
  'Sounders': { league: 'mls', sport: 'soccer' },
  'St. Louis City SC': { league: 'mls', sport: 'soccer' },
  'Toronto FC': { league: 'mls', sport: 'soccer' },
  'Vancouver Whitecaps': { league: 'mls', sport: 'soccer' },
  'Whitecaps': { league: 'mls', sport: 'soccer' },

  // === NCAAB (College Basketball - Top Programs) ===
  'Duke': { league: 'ncaab', sport: 'basketball' },
  'Blue Devils': { league: 'ncaab', sport: 'basketball' },
  'North Carolina': { league: 'ncaab', sport: 'basketball' },
  'Tar Heels': { league: 'ncaab', sport: 'basketball' },
  'UNC': { league: 'ncaab', sport: 'basketball' },
  'Kentucky': { league: 'ncaab', sport: 'basketball' },
  'Wildcats': { league: 'ncaab', sport: 'basketball' }, // Multiple schools
  'Kansas': { league: 'ncaab', sport: 'basketball' },
  'Jayhawks': { league: 'ncaab', sport: 'basketball' },
  'Gonzaga': { league: 'ncaab', sport: 'basketball' },
  'Bulldogs': { league: 'ncaab', sport: 'basketball' },
  'Villanova': { league: 'ncaab', sport: 'basketball' },
  'Michigan': { league: 'ncaab', sport: 'basketball' },
  'Wolverines': { league: 'ncaab', sport: 'basketball' },
  'Michigan State': { league: 'ncaab', sport: 'basketball' },
  'Spartans': { league: 'ncaab', sport: 'basketball' },
  'MSU': { league: 'ncaab', sport: 'basketball' },
  'Ohio State': { league: 'ncaab', sport: 'basketball' },
  'Buckeyes': { league: 'ncaab', sport: 'basketball' },
  'Purdue': { league: 'ncaab', sport: 'basketball' },
  'Boilermakers': { league: 'ncaab', sport: 'basketball' },
  'Indiana': { league: 'ncaab', sport: 'basketball' },
  'Hoosiers': { league: 'ncaab', sport: 'basketball' },
  'Illinois': { league: 'ncaab', sport: 'basketball' },
  'Fighting Illini': { league: 'ncaab', sport: 'basketball' },
  'Illini': { league: 'ncaab', sport: 'basketball' },
  'Wisconsin': { league: 'ncaab', sport: 'basketball' },
  'Badgers': { league: 'ncaab', sport: 'basketball' },
  'Iowa': { league: 'ncaab', sport: 'basketball' },
  'Hawkeyes': { league: 'ncaab', sport: 'basketball' },
  'Maryland': { league: 'ncaab', sport: 'basketball' },
  'Terrapins': { league: 'ncaab', sport: 'basketball' },
  'Terps': { league: 'ncaab', sport: 'basketball' },
  'Penn State': { league: 'ncaab', sport: 'basketball' },
  'Nittany Lions': { league: 'ncaab', sport: 'basketball' },
  'Nebraska': { league: 'ncaab', sport: 'basketball' },
  'Cornhuskers': { league: 'ncaab', sport: 'basketball' },
  'Northwestern': { league: 'ncaab', sport: 'basketball' },
  'Rutgers': { league: 'ncaab', sport: 'basketball' },
  'Scarlet Knights': { league: 'ncaab', sport: 'basketball' },
  'Baylor': { league: 'ncaab', sport: 'basketball' },
  'Texas': { league: 'ncaab', sport: 'basketball' },
  'Longhorns': { league: 'ncaab', sport: 'basketball' },
  'Texas Tech': { league: 'ncaab', sport: 'basketball' },
  'Red Raiders': { league: 'ncaab', sport: 'basketball' },
  'Oklahoma': { league: 'ncaab', sport: 'basketball' },
  'Sooners': { league: 'ncaab', sport: 'basketball' },
  'Oklahoma State': { league: 'ncaab', sport: 'basketball' },
  'Cowboys': { league: 'ncaab', sport: 'basketball' }, // Also NFL
  'Kansas State': { league: 'ncaab', sport: 'basketball' },
  'Iowa State': { league: 'ncaab', sport: 'basketball' },
  'Cyclones': { league: 'ncaab', sport: 'basketball' },
  'West Virginia': { league: 'ncaab', sport: 'basketball' },
  'Mountaineers': { league: 'ncaab', sport: 'basketball' },
  'TCU': { league: 'ncaab', sport: 'basketball' },
  'Horned Frogs': { league: 'ncaab', sport: 'basketball' },
  'Tennessee': { league: 'ncaab', sport: 'basketball' },
  'Volunteers': { league: 'ncaab', sport: 'basketball' },
  'Vols': { league: 'ncaab', sport: 'basketball' },
  'Alabama': { league: 'ncaab', sport: 'basketball' },
  'Crimson Tide': { league: 'ncaab', sport: 'basketball' },
  'Auburn': { league: 'ncaab', sport: 'basketball' },
  'Florida': { league: 'ncaab', sport: 'basketball' },
  'Gators': { league: 'ncaab', sport: 'basketball' },
  'Arkansas': { league: 'ncaab', sport: 'basketball' },
  'Razorbacks': { league: 'ncaab', sport: 'basketball' },
  'LSU': { league: 'ncaab', sport: 'basketball' },
  'Ole Miss': { league: 'ncaab', sport: 'basketball' },
  'Rebels': { league: 'ncaab', sport: 'basketball' },
  'Mississippi State': { league: 'ncaab', sport: 'basketball' },
  'Missouri': { league: 'ncaab', sport: 'basketball' },
  'Mizzou': { league: 'ncaab', sport: 'basketball' },
  'Texas A&M': { league: 'ncaab', sport: 'basketball' },
  'Aggies': { league: 'ncaab', sport: 'basketball' },
  'South Carolina': { league: 'ncaab', sport: 'basketball' },
  'Gamecocks': { league: 'ncaab', sport: 'basketball' },
  'Georgia': { league: 'ncaab', sport: 'basketball' },
  'Vanderbilt': { league: 'ncaab', sport: 'basketball' },
  'Commodores': { league: 'ncaab', sport: 'basketball' },
  'UCLA': { league: 'ncaab', sport: 'basketball' },
  'Bruins': { league: 'ncaab', sport: 'basketball' }, // Also NHL Boston
  'USC': { league: 'ncaab', sport: 'basketball' },
  'Trojans': { league: 'ncaab', sport: 'basketball' },
  'Arizona': { league: 'ncaab', sport: 'basketball' },
  'Oregon': { league: 'ncaab', sport: 'basketball' },
  'Ducks': { league: 'ncaab', sport: 'basketball' }, // Also NHL Anaheim
  'Stanford': { league: 'ncaab', sport: 'basketball' },
  'Cardinal': { league: 'ncaab', sport: 'basketball' },
  'Colorado': { league: 'ncaab', sport: 'basketball' },
  'Buffaloes': { league: 'ncaab', sport: 'basketball' },
  'Buffs': { league: 'ncaab', sport: 'basketball' },
  'Utah': { league: 'ncaab', sport: 'basketball' },
  'Utes': { league: 'ncaab', sport: 'basketball' },
  'Arizona State': { league: 'ncaab', sport: 'basketball' },
  'Sun Devils': { league: 'ncaab', sport: 'basketball' },
  'ASU': { league: 'ncaab', sport: 'basketball' },
  'California': { league: 'ncaab', sport: 'basketball' },
  'Golden Bears': { league: 'ncaab', sport: 'basketball' },
  'Cal': { league: 'ncaab', sport: 'basketball' },
  'Oregon State': { league: 'ncaab', sport: 'basketball' },
  'Beavers': { league: 'ncaab', sport: 'basketball' },
  'Washington State': { league: 'ncaab', sport: 'basketball' },
  'Cougars': { league: 'ncaab', sport: 'basketball' },
  'Virginia': { league: 'ncaab', sport: 'basketball' },
  'Cavaliers': { league: 'ncaab', sport: 'basketball' }, // Also NBA Cleveland
  'Virginia Tech': { league: 'ncaab', sport: 'basketball' },
  'Hokies': { league: 'ncaab', sport: 'basketball' },
  'Miami': { league: 'ncaab', sport: 'basketball' },
  'Hurricanes': { league: 'ncaab', sport: 'basketball' }, // Also NHL Carolina
  'Syracuse': { league: 'ncaab', sport: 'basketball' },
  'Orange': { league: 'ncaab', sport: 'basketball' },
  'Louisville': { league: 'ncaab', sport: 'basketball' },
  'Clemson': { league: 'ncaab', sport: 'basketball' },
  'Notre Dame': { league: 'ncaab', sport: 'basketball' },
  'Fighting Irish': { league: 'ncaab', sport: 'basketball' },
  'NC State': { league: 'ncaab', sport: 'basketball' },
  'Wolfpack': { league: 'ncaab', sport: 'basketball' },
  'Wake Forest': { league: 'ncaab', sport: 'basketball' },
  'Demon Deacons': { league: 'ncaab', sport: 'basketball' },
  'Deacs': { league: 'ncaab', sport: 'basketball' },
  'Boston College': { league: 'ncaab', sport: 'basketball' },
  'Florida State': { league: 'ncaab', sport: 'basketball' },
  'Seminoles': { league: 'ncaab', sport: 'basketball' },
  'FSU': { league: 'ncaab', sport: 'basketball' },
  'Georgia Tech': { league: 'ncaab', sport: 'basketball' },
  'Yellow Jackets': { league: 'ncaab', sport: 'basketball' },
  'Pittsburgh': { league: 'ncaab', sport: 'basketball' },
  'Pitt': { league: 'ncaab', sport: 'basketball' },
  'UConn': { league: 'ncaab', sport: 'basketball' },
  'Connecticut': { league: 'ncaab', sport: 'basketball' },
  'Huskies': { league: 'ncaab', sport: 'basketball' },
  'Creighton': { league: 'ncaab', sport: 'basketball' },
  'Bluejays': { league: 'ncaab', sport: 'basketball' },
  'Xavier': { league: 'ncaab', sport: 'basketball' },
  'Musketeers': { league: 'ncaab', sport: 'basketball' },
  'Butler': { league: 'ncaab', sport: 'basketball' },
  'Marquette': { league: 'ncaab', sport: 'basketball' },
  'Golden Eagles': { league: 'ncaab', sport: 'basketball' },
  'Providence': { league: 'ncaab', sport: 'basketball' },
  'Friars': { league: 'ncaab', sport: 'basketball' },
  "St. John's": { league: 'ncaab', sport: 'basketball' },
  'Seton Hall': { league: 'ncaab', sport: 'basketball' },
  'Georgetown': { league: 'ncaab', sport: 'basketball' },
  'Hoyas': { league: 'ncaab', sport: 'basketball' },
  'DePaul': { league: 'ncaab', sport: 'basketball' },
  'Blue Demons': { league: 'ncaab', sport: 'basketball' },
  'Memphis': { league: 'ncaab', sport: 'basketball' },
  'Houston': { league: 'ncaab', sport: 'basketball' },
  'Cincinnati': { league: 'ncaab', sport: 'basketball' },
  'Bearcats': { league: 'ncaab', sport: 'basketball' },
  'SMU': { league: 'ncaab', sport: 'basketball' },
  'Mustangs': { league: 'ncaab', sport: 'basketball' },
  'UCF': { league: 'ncaab', sport: 'basketball' },
  'BYU': { league: 'ncaab', sport: 'basketball' },
  "Saint Mary's": { league: 'ncaab', sport: 'basketball' },
  'Gaels': { league: 'ncaab', sport: 'basketball' },
  'San Diego State': { league: 'ncaab', sport: 'basketball' },
  'SDSU': { league: 'ncaab', sport: 'basketball' },
  'Aztecs': { league: 'ncaab', sport: 'basketball' },
  'Nevada': { league: 'ncaab', sport: 'basketball' },
  'Wolf Pack': { league: 'ncaab', sport: 'basketball' },
  'Boise State': { league: 'ncaab', sport: 'basketball' },
  'Broncos': { league: 'ncaab', sport: 'basketball' }, // Also NFL Denver
  'UNLV': { league: 'ncaab', sport: 'basketball' },
  'Runnin Rebels': { league: 'ncaab', sport: 'basketball' },
  'VCU': { league: 'ncaab', sport: 'basketball' },
  'Davidson': { league: 'ncaab', sport: 'basketball' },
  'Dayton': { league: 'ncaab', sport: 'basketball' },
  'Flyers': { league: 'ncaab', sport: 'basketball' }, // Also NHL Philadelphia
  'Richmond': { league: 'ncaab', sport: 'basketball' },
  'Spiders': { league: 'ncaab', sport: 'basketball' },
  "St. Bonaventure": { league: 'ncaab', sport: 'basketball' },
  'Bonnies': { league: 'ncaab', sport: 'basketball' },
  
  // === NCAAF (College Football - Top Programs) ===
  // Note: Most college programs share basketball and football teams
  // The context (market type, season) will help disambiguate
  // Adding some football-specific teams
  'Air Force': { league: 'ncaaf', sport: 'football' },
  'Army': { league: 'ncaaf', sport: 'football' },
  'Black Knights': { league: 'ncaaf', sport: 'football' },
  'Navy': { league: 'ncaaf', sport: 'football' },
  'Midshipmen': { league: 'ncaaf', sport: 'football' },
  'Marshall': { league: 'ncaaf', sport: 'football' },
  'Thundering Herd': { league: 'ncaaf', sport: 'football' },
  'Coastal Carolina': { league: 'ncaaf', sport: 'football' },
  'Chanticleers': { league: 'ncaaf', sport: 'football' },
  'Appalachian State': { league: 'ncaaf', sport: 'football' },
  'App State': { league: 'ncaaf', sport: 'football' },
  'Mountaineers': { league: 'ncaaf', sport: 'football' },
  'Liberty': { league: 'ncaaf', sport: 'football' }, // Also WNBA
  'Flames': { league: 'ncaaf', sport: 'football' }, // Also NHL Calgary
  'James Madison': { league: 'ncaaf', sport: 'football' },
  'JMU': { league: 'ncaaf', sport: 'football' },
  'Dukes': { league: 'ncaaf', sport: 'football' },
  'Troy': { league: 'ncaaf', sport: 'football' },
  'Trojans': { league: 'ncaaf', sport: 'football' },
  'Louisiana': { league: 'ncaaf', sport: 'football' },
  'Ragin Cajuns': { league: 'ncaaf', sport: 'football' },
  'ULL': { league: 'ncaaf', sport: 'football' },
};
